import React, { useMemo, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  CalendarClock,
  CheckCircle2,
  Clock3,
  Plus,
  Mail,
  ShieldCheck,
  Sparkles,
  Trash2,
  UserPlus,
  UserRoundCheck,
  Video,
  X,
} from "lucide-react";

const cloudFunctions = getFunctions(undefined, "asia-south1");

function defaultStartValue() {
  const date = new Date(Date.now() + 24 * 60 * 60 * 1000);
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value])
  );
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:00`;
}

function parseIstInput(value) {
  if (!value) return new Date(Number.NaN);
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (/Z$|[+-]\d{2}:?\d{2}$/.test(value)) return new Date(value);
  return new Date(`${value.length === 16 ? `${value}:00` : value}+05:30`);
}

function formatDateTime(value) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "full",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  }).format(parseIstInput(value));
}

const initialForm = {
  scheduledStart: defaultStartValue(),
  durationMinutes: 60,
  round: "HR",
  notes: "",
};

function createPanelMember(overrides = {}) {
  return {
    id: `panel_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    name: "",
    email: "",
    designation: "",
    memberType: "INTERNAL",
    userId: "",
    canScore: true,
    ...overrides,
  };
}

export default function InterviewSchedulerModal({
  candidate,
  currentUser,
  firebaseFunctions,
  onClose,
  onScheduled,
}) {
  const [form, setForm] = useState(initialForm);
  const [panelMembers, setPanelMembers] = useState(() => [
    createPanelMember({
      name: currentUser?.name || "",
      email: currentUser?.email || "",
      designation: currentUser?.designation || currentUser?.role || "Interview Host",
      userId: currentUser?.uid || currentUser?.id || "",
      memberType: "INTERNAL",
    }),
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const endTime = useMemo(() => {
    const start = parseIstInput(form.scheduledStart).getTime();
    if (!Number.isFinite(start)) return "";
    return new Date(start + Number(form.durationMinutes) * 60 * 1000);
  }, [form.scheduledStart, form.durationMinutes]);

  const update = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));

  const updatePanelMember = (id, key, value) =>
    setPanelMembers((current) =>
      current.map((member) =>
        member.id === id ? { ...member, [key]: value } : member
      )
    );

  const addPanelMember = () => {
    if (panelMembers.length >= 12) {
      setError("Maximum 12 panel members / experts add kiye ja sakte hain.");
      return;
    }
    setError("");
    setPanelMembers((current) => [
      ...current,
      createPanelMember({ memberType: "EXTERNAL", designation: "Subject Expert" }),
    ]);
  };

  const removePanelMember = (id) => {
    if (panelMembers.length === 1) {
      setError("Kam se kam ek interview host / panel member required hai.");
      return;
    }
    setPanelMembers((current) => current.filter((member) => member.id !== id));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");

    if (!candidate?.id || !candidate?.candidateUserId) {
      setError(
        "Is candidate ka portal account link nahi hai. Candidate ko Career Portal se account banakar apply karna hoga."
      );
      return;
    }
    if (!candidate?.email) {
      setError("Candidate email available nahi hai.");
      return;
    }
    if (
      !form.scheduledStart ||
      parseIstInput(form.scheduledStart).getTime() <= Date.now()
    ) {
      setError("Future interview date aur time select kijiye.");
      return;
    }
    const cleanedPanelMembers = panelMembers.map((member) => ({
      ...member,
      name: member.name.trim(),
      email: member.email.trim().toLowerCase(),
      designation: member.designation.trim(),
      userId: member.userId.trim(),
    }));
    if (cleanedPanelMembers.some((member) => !member.name || !member.email)) {
      setError("Har panel member / expert ka name aur email required hai.");
      return;
    }
    const uniqueEmails = new Set(cleanedPanelMembers.map((member) => member.email));
    if (uniqueEmails.size !== cleanedPanelMembers.length) {
      setError("Har panel member ka email alag hona chahiye.");
      return;
    }

    setSaving(true);
    try {
      const scheduleVideoInterview = httpsCallable(
        firebaseFunctions || cloudFunctions,
        "scheduleVideoInterview"
      );
      const response = await scheduleVideoInterview({
        applicationId: candidate.id,
        candidateUserId: candidate.candidateUserId,
        candidateName: candidate.candidateName || candidate.name || "",
        candidateEmail: candidate.email,
        candidateMobile: candidate.mobile || "",
        jobTitle: candidate.jobTitle || candidate.position || "",
        department: candidate.deptName || candidate.department || "",
        scheduledStart: form.scheduledStart,
        durationMinutes: Number(form.durationMinutes),
        timeZone: "Asia/Kolkata",
        round: form.round,
        panelName: cleanedPanelMembers[0]?.name || "",
        panelNames: cleanedPanelMembers.map((member) => member.name),
        panelUserIds: cleanedPanelMembers
          .filter((member) => member.memberType === "INTERNAL" && member.userId)
          .map((member) => member.userId),
        panelMembers: cleanedPanelMembers,
        notes: form.notes,
        idempotencyKey: `${candidate.id}:${form.round}:${form.scheduledStart}:Asia/Kolkata`,
      });
      onScheduled?.(response.data);
    } catch (scheduleError) {
      console.error("Interview scheduling failed", scheduleError);
      setError(
        scheduleError?.message ||
          "Secure video interview create nahi hua. Cloud Function configuration check kijiye."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/75 p-3 backdrop-blur-md sm:p-6">
      <div className="max-h-[96vh] w-full max-w-5xl overflow-y-auto rounded-[30px] border border-white/20 bg-[#f4f7fb] shadow-[0_40px_120px_rgba(2,8,23,.5)]">
        <header className="relative overflow-hidden rounded-t-[30px] bg-[#071b33] px-5 py-6 text-white sm:px-8">
          <div className="absolute inset-y-0 right-0 w-2/3 bg-[radial-gradient(circle_at_80%_30%,rgba(37,99,235,.45),transparent_48%)]" />
          <div className="relative flex items-start justify-between gap-5">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/25 bg-cyan-300/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[.18em] text-cyan-100">
                <Sparkles size={13} /> Secure Interview Automation
              </div>
              <h2 className="mt-4 text-2xl font-black sm:text-3xl">
                Schedule Live Video Interview
              </h2>
              <p className="mt-2 max-w-2xl text-sm font-medium leading-6 text-slate-300">
                Candidate identity, private room, email invite, calendar file,
                one-hour expiry and audit trail automatically generate honge.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 transition hover:bg-white/20"
              aria-label="Close"
            >
              <X size={19} />
            </button>
          </div>
        </header>

        <form onSubmit={submit} className="grid lg:grid-cols-[1fr_320px]">
          <div className="space-y-5 p-5 sm:p-8">
            {error && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">
                {error}
              </div>
            )}

            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-[.2em] text-blue-600">
                Candidate
              </p>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-xl font-black text-slate-950">
                    {candidate?.candidateName || candidate?.name || "-"}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-slate-500">
                    {candidate?.jobTitle || candidate?.position || "-"} •{" "}
                    {candidate?.email || "-"}
                  </p>
                </div>
                <div className="rounded-2xl bg-emerald-50 px-4 py-3 text-emerald-700">
                  <p className="text-[9px] font-black uppercase tracking-wider">
                    Portal identity
                  </p>
                  <p className="mt-1 text-sm font-black">
                    {candidate?.candidateUserId ? "Verified" : "Not linked"}
                  </p>
                </div>
              </div>
            </section>

            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="grid gap-4 sm:grid-cols-2">
                <label>
                  <span className="mb-1.5 block text-[10px] font-black uppercase tracking-wider text-slate-500">
                    Interview Round
                  </span>
                  <select
                    value={form.round}
                    onChange={(event) => update("round", event.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-3.5 py-3 text-sm font-bold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  >
                    <option value="HR">HR Round</option>
                    <option value="TECHNICAL">Technical Round</option>
                    <option value="MANAGEMENT">Management Round</option>
                    <option value="CEO">CEO Final Round</option>
                  </select>
                </label>
                <label>
                  <span className="mb-1.5 block text-[10px] font-black uppercase tracking-wider text-slate-500">
                    Date & Time
                  </span>
                  <input
                    type="datetime-local"
                    value={form.scheduledStart}
                    min={defaultStartValue().slice(0, 10)}
                    onChange={(event) =>
                      update("scheduledStart", event.target.value)
                    }
                    className="w-full rounded-xl border border-slate-200 px-3.5 py-3 text-sm font-bold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    required
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-[10px] font-black uppercase tracking-wider text-slate-500">
                    Duration
                  </span>
                  <select
                    value={form.durationMinutes}
                    onChange={(event) => update("durationMinutes", Number(event.target.value))}
                    className="h-[46px] w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm font-black text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  >
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>60 minutes</option>
                    <option value={90}>90 minutes</option>
                    <option value={120}>120 minutes</option>
                  </select>
                </label>
                <label>
                  <span className="mb-1.5 block text-[10px] font-black uppercase tracking-wider text-slate-500">
                    Time Zone
                  </span>
                  <div className="flex h-[46px] items-center rounded-xl border border-slate-200 bg-slate-50 px-3.5 text-sm font-black text-slate-700">
                    India Standard Time (IST)
                  </div>
                </label>
              </div>

              <div className="mt-5 border-t border-slate-100 pt-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="flex items-center gap-2 text-sm font-black text-slate-900">
                      <UserPlus size={18} className="text-blue-600" /> Interview Panel & Experts
                    </p>
                    <p className="mt-1 text-xs font-semibold text-slate-500">
                      Har member ko private, individual join link aur calendar email milega.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={addPanelMember}
                    className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3.5 py-2.5 text-xs font-black text-blue-700 transition hover:bg-blue-100"
                  >
                    <Plus size={15} /> Add Expert / Member
                  </button>
                </div>

                <div className="mt-4 space-y-3">
                  {panelMembers.map((member, index) => (
                    <div
                      key={member.id}
                      className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4"
                    >
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-600 text-[11px] font-black text-white">
                            {index + 1}
                          </span>
                          <p className="text-xs font-black uppercase tracking-wider text-slate-600">
                            {member.memberType === "EXTERNAL" ? "External Expert" : "Internal Panel"}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removePanelMember(member.id)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                          aria-label={`Remove panel member ${index + 1}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <label>
                          <span className="mb-1 block text-[9px] font-black uppercase tracking-wider text-slate-500">Member Type</span>
                          <select
                            value={member.memberType}
                            onChange={(event) => updatePanelMember(member.id, "memberType", event.target.value)}
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                          >
                            <option value="INTERNAL">Internal Team Member</option>
                            <option value="EXTERNAL">External Subject Expert</option>
                          </select>
                        </label>
                        <label>
                          <span className="mb-1 block text-[9px] font-black uppercase tracking-wider text-slate-500">Full Name *</span>
                          <input
                            value={member.name}
                            onChange={(event) => updatePanelMember(member.id, "name", event.target.value)}
                            placeholder="e.g. Dr. Amit Shah"
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                            required
                          />
                        </label>
                        <label>
                          <span className="mb-1 block text-[9px] font-black uppercase tracking-wider text-slate-500">Email Address *</span>
                          <input
                            type="email"
                            value={member.email}
                            onChange={(event) => updatePanelMember(member.id, "email", event.target.value)}
                            placeholder="expert@example.com"
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                            required
                          />
                        </label>
                        <label>
                          <span className="mb-1 block text-[9px] font-black uppercase tracking-wider text-slate-500">Designation / Expertise</span>
                          <input
                            value={member.designation}
                            onChange={(event) => updatePanelMember(member.id, "designation", event.target.value)}
                            placeholder="e.g. Civil Engineering Expert"
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                          />
                        </label>
                      </div>

                      <label className="mt-3 flex items-center gap-2 text-xs font-bold text-slate-600">
                        <input
                          type="checkbox"
                          checked={member.canScore}
                          onChange={(event) => updatePanelMember(member.id, "canScore", event.target.checked)}
                          className="h-4 w-4 rounded border-slate-300 text-blue-600"
                        />
                        Interview scorecard / feedback submit kar sakte hain
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <label className="mt-4 block">
                <span className="mb-1.5 block text-[10px] font-black uppercase tracking-wider text-slate-500">
                  Candidate Instructions / Notes
                </span>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(event) => update("notes", event.target.value)}
                  placeholder="Keep original documents ready, join from a quiet place..."
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-3 text-sm font-semibold leading-6 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                />
              </label>

              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-black text-slate-800">
                  Privacy-first session
                </p>
                <p className="mt-1 text-xs font-semibold leading-5 text-slate-500">
                  Recording is disabled in this package. Enable it only after an
                  approved retention policy, provider plan and explicit candidate
                  consent workflow are configured.
                </p>
              </div>
            </section>
          </div>

          <aside className="border-t border-slate-200 bg-white p-5 lg:border-l lg:border-t-0 sm:p-7">
            <p className="text-[10px] font-black uppercase tracking-[.2em] text-blue-600">
              Automation Preview
            </p>
            <div className="mt-4 rounded-3xl bg-[#071b33] p-5 text-white">
              <CalendarClock size={23} className="text-cyan-300" />
              <p className="mt-4 text-lg font-black">
                {formatDateTime(form.scheduledStart)}
              </p>
              <p className="mt-2 text-xs font-semibold leading-5 text-slate-300">
                Ends at {formatDateTime(endTime)}. Video token automatically
                expire hoga.
              </p>
            </div>

            <div className="mt-5 space-y-3">
              {[
                [Video, "Private browser video room"],
                [Mail, "Email + calendar invitation"],
                [Clock3, "One-hour secure access"],
                [UserRoundCheck, "Candidate waiting notification"],
                [ShieldCheck, "Identity and role verification"],
                [CheckCircle2, "Scorecard and audit trail"],
              ].map(([Icon, label]) => (
                <div
                  key={label}
                  className="flex items-center gap-3 rounded-2xl bg-slate-50 p-3"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                    <Icon size={17} />
                  </div>
                  <p className="text-xs font-black text-slate-700">{label}</p>
                </div>
              ))}
            </div>

            <button
              type="submit"
              disabled={saving}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3.5 text-sm font-black text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? (
                "Creating secure room..."
              ) : (
                <>
                  <Video size={18} /> Schedule & Send Link
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 w-full rounded-2xl px-4 py-3 text-xs font-black text-slate-500 transition hover:bg-slate-100"
            >
              Cancel
            </button>
          </aside>
        </form>
      </div>
    </div>
  );
}
